import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus } from 'lucide-react';
import { controllingAPI, financeAPI, masterDataAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const Controlling = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'operating-concerns';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  const [data, setData] = useState({
    operatingConcerns: [],
    controllingAreas: [],
    costCenterGroups: [],
    costCenters: [],
    profitCenterGroups: [],
    profitCenters: [],
    internalOrders: [],
    statisticalKeyFigures: [],
  });

  // Foreign key data
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [fiscalYearVariants, setFiscalYearVariants] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const { canWrite } = useAuth();

  // State for dropdowns
  const [selectedOC, setSelectedOC] = useState('');
  const [selectedCA, setSelectedCA] = useState('');
  const [selectedCOA, setSelectedCOA] = useState('');
  const [selectedFYV, setSelectedFYV] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedAssignmentControl, setSelectedAssignmentControl] = useState('');
  const [selectedCurrencyType, setSelectedCurrencyType] = useState('');
  const [selectedNodeType, setSelectedNodeType] = useState('');
  const [selectedParentNode, setSelectedParentNode] = useState('');
  const [selectedParentGroup, setSelectedParentGroup] = useState('');
  const [selectedHierarchyNode, setSelectedHierarchyNode] = useState('');
  const [selectedCostCenterGroup, setSelectedCostCenterGroup] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProfitCenterGroup, setSelectedProfitCenterGroup] = useState('');
  const [selectedProfitCenter, setSelectedProfitCenter] = useState('');
  const [selectedCostCenter, setSelectedCostCenter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  // Master-data dropdown state (FICO Phase 2 refactor)
  const [selectedPersonResponsible, setSelectedPersonResponsible] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('');
  const [selectedUOM, setSelectedUOM] = useState('');
  // Master-data lists
  const [employees, setEmployees] = useState([]);
  const [segments, setSegments] = useState([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);

  const ORDER_TYPES = [
    { value: 'CAPEX', label: 'CAPEX - Capital Expenditure' },
    { value: 'OVERHEAD', label: 'OVERHEAD - General Overhead' },
    { value: 'MARKETING', label: 'MARKETING - Marketing Campaign' },
    { value: 'PRODUCTION', label: 'PRODUCTION - Production Order' },
    { value: 'MAINTENANCE', label: 'MAINTENANCE - Repair / Maintenance' },
    { value: 'IT', label: 'IT - IT Project' },
    { value: 'R&D', label: 'R&D - Research & Development' },
    { value: 'OTHER', label: 'OTHER' },
  ];

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    loadData();
    loadForeignKeyData();
  }, []);

  // Initialize form states from the editing item (hoisted out of getFormFields()
  // so the Hook runs at the component level, not inside a render helper).
  useEffect(() => {
    if (editingItem) {
      setSelectedOC(editingItem.operating_concern_id || '');
      setSelectedCA(editingItem.controlling_area_id || '');
      setSelectedCOA(editingItem.chart_of_accounts_id || '');
      setSelectedFYV(editingItem.fiscal_year_variant_id || '');
      setSelectedCurrency(editingItem.currency || editingItem.area_currency || '');
      setSelectedAssignmentControl(editingItem.assignment_control || '');
      setSelectedCurrencyType(editingItem.currency_type || '20');
      setSelectedNodeType(editingItem.node_type || '');
      setSelectedParentNode(editingItem.parent_node_id || '');
      setSelectedParentGroup(editingItem.parent_group_id || '');
      setSelectedHierarchyNode(editingItem.standard_hierarchy_node || '');
      setSelectedProfitCenterGroup(editingItem.profit_center_group_id || '');
      setSelectedProfitCenter(editingItem.profit_center_id || '');
      setSelectedCostCenter(editingItem.assigned_cost_center_id || '');
      setSelectedCostCenterGroup(editingItem.group_id || '');
      setSelectedCategory(editingItem.category || 'Admin');
      setSelectedStatus(editingItem.status || 'Active');
      setSelectedPersonResponsible(editingItem.person_responsible_id || '');
      setSelectedSegment(editingItem.segment_id || '');
      setSelectedOrderType(editingItem.order_type || '');
      setSelectedUOM(editingItem.unit_of_measure || '');
    } else {
      setSelectedOC('');
      setSelectedCA('');
      setSelectedCOA('');
      setSelectedFYV('');
      setSelectedCurrency('');
      setSelectedAssignmentControl('');
      setSelectedCurrencyType('20');
      setSelectedNodeType('');
      setSelectedParentNode('');
      setSelectedParentGroup('');
      setSelectedHierarchyNode('');
      setSelectedProfitCenterGroup('');
      setSelectedProfitCenter('');
      setSelectedCostCenter('');
      setSelectedCostCenterGroup('');
      setSelectedCategory('Admin');
      setSelectedStatus('Active');
      setSelectedPersonResponsible('');
      setSelectedSegment('');
      setSelectedOrderType('');
      setSelectedUOM('');
    }
  }, [editingItem]);

  const loadForeignKeyData = async () => {
    try {
      const [coaRes, fyvRes, currRes, empRes, segRes, uomRes] = await Promise.all([
        financeAPI.getChartOfAccounts(),
        financeAPI.getFiscalYearVariants(),
        masterDataAPI.getCurrencies(),
        // Use axios directly to keep interface lean; api wrapper exists too.
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hcm/employees`).then((r) => r.json()).catch(() => []),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance/segments`).then((r) => r.json()).catch(() => []),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/units-of-measure`).then((r) => r.json()).catch(() => []),
      ]);
      setChartOfAccounts(coaRes.data);
      setFiscalYearVariants(fyvRes.data);
      setCurrencies(currRes.data);
      setEmployees(Array.isArray(empRes) ? empRes : (empRes.employees || []));
      setSegments(Array.isArray(segRes) ? segRes : []);
      setUnitsOfMeasure(Array.isArray(uomRes) ? uomRes : []);
    } catch (error) {
      console.error('Failed to load foreign key data:', error);
    }
  };

  const loadData = async () => {
    try {
      const [oc, ca, ccg, cc, pcg, pc, io, skf] = await Promise.all([
        controllingAPI.getOperatingConcerns(),
        controllingAPI.getControllingAreas(),
        controllingAPI.getCostCenterGroups(),
        controllingAPI.getCostCenters(),
        controllingAPI.getProfitCenterGroups(),
        controllingAPI.getProfitCenters(),
        controllingAPI.getInternalOrders(),
        controllingAPI.getStatisticalKeyFigures(),
      ]);
      
      setData({
        operatingConcerns: oc.data,
        controllingAreas: ca.data,
        costCenterGroups: ccg.data,
        costCenters: cc.data,
        profitCenterGroups: pcg.data,
        profitCenters: pc.data,
        internalOrders: io.data,
        statisticalKeyFigures: skf.data,
      });
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formDataObj = {};
    
    formData.forEach((value, key) => {
      // Convert empty strings to null for optional fields
      if (value === '') {
        formDataObj[key] = null;
      } else {
        formDataObj[key] = value;
      }
    });

    try {
      const id = editingItem?.[getIdField()];
      
      switch (activeTab) {
        case 'operating-concerns':
          editingItem
            ? await controllingAPI.updateOperatingConcern(id, formDataObj)
            : await controllingAPI.createOperatingConcern(formDataObj);
          break;
        case 'controlling-areas':
          editingItem
            ? await controllingAPI.updateControllingArea(id, formDataObj)
            : await controllingAPI.createControllingArea(formDataObj);
          break;
        case 'cost-center-groups':
          if (editingItem) {
            const ca_id = editingItem.controlling_area_id;
            const group_id = editingItem.group_id;
            await controllingAPI.updateCostCenterGroup(ca_id, group_id, formDataObj);
          } else {
            await controllingAPI.createCostCenterGroup(formDataObj);
          }
          break;
        case 'cost-centers':
          if (editingItem) {
            const ca_id = editingItem.controlling_area_id;
            const cc_id = editingItem.cost_center_id;
            await controllingAPI.updateCostCenter(ca_id, cc_id, formDataObj);
          } else {
            await controllingAPI.createCostCenter(formDataObj);
          }
          break;
        case 'profit-center-groups':
          if (editingItem) {
            const ca_id = editingItem.controlling_area_id;
            const group_id = editingItem.group_id;
            await controllingAPI.updateProfitCenterGroup(ca_id, group_id, formDataObj);
          } else {
            await controllingAPI.createProfitCenterGroup(formDataObj);
          }
          break;
        case 'profit-centers':
          if (editingItem) {
            const ca_id = editingItem.controlling_area_id;
            const pc_id = editingItem.profit_center_id;
            await controllingAPI.updateProfitCenter(ca_id, pc_id, formDataObj);
          } else {
            await controllingAPI.createProfitCenter(formDataObj);
          }
          break;
        case 'internal-orders':
          editingItem
            ? await controllingAPI.updateInternalOrder(id, formDataObj)
            : await controllingAPI.createInternalOrder(formDataObj);
          break;
        case 'statistical-key-figures':
          editingItem
            ? await controllingAPI.updateStatisticalKeyFigure(id, formDataObj)
            : await controllingAPI.createStatisticalKeyFigure(formDataObj);
          break;
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      switch (activeTab) {
        case 'operating-concerns':
          await controllingAPI.deleteOperatingConcern(item.operating_concern_id);
          break;
        case 'controlling-areas':
          await controllingAPI.deleteControllingArea(item.controlling_area_id);
          break;
        case 'cost-center-groups':
          await controllingAPI.deleteCostCenterGroup(item.controlling_area_id, item.group_id);
          break;
        case 'cost-centers':
          await controllingAPI.deleteCostCenter(item.controlling_area_id, item.cost_center_id);
          break;
        case 'profit-center-groups':
          await controllingAPI.deleteProfitCenterGroup(item.controlling_area_id, item.group_id);
          break;
        case 'profit-centers':
          await controllingAPI.deleteProfitCenter(item.controlling_area_id, item.profit_center_id);
          break;
        case 'internal-orders':
          await controllingAPI.deleteInternalOrder(item.internal_order_id);
          break;
        case 'statistical-key-figures':
          await controllingAPI.deleteStatisticalKeyFigure(item.key_figure_id);
          break;
      }
      loadData();
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const getIdField = () => {
    const idMap = {
      'operating-concerns': 'operating_concern_id',
      'controlling-areas': 'controlling_area_id',
      'cost-center-groups': 'group_id',
      'cost-centers': 'cost_center_id',
      'profit-center-groups': 'group_id',
      'profit-centers': 'profit_center_id',
      'internal-orders': 'internal_order_id',
      'statistical-key-figures': 'skf_id',
    };
    return idMap[activeTab];
  };

  const getFormFields = () => {
    switch (activeTab) {
      case 'operating-concerns':
        return (
          <>
            <div>
              <Label>Operating Concern ID</Label>
              <Input 
                name="operating_concern_id" 
                defaultValue={editingItem?.operating_concern_id}
                required 
                disabled={!!editingItem}
                placeholder="e.g., OC01"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required placeholder="e.g., Global Operating Concern" />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} placeholder="Optional description" />
            </div>
          </>
        );

      case 'controlling-areas':
        return (
          <>
            <div>
              <Label>Controlling Area ID</Label>
              <Input 
                name="controlling_area_id"
                defaultValue={editingItem?.controlling_area_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., CA01"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required />
            </div>
            <div>
              <Label>Operating Concern</Label>
              <Select value={selectedOC} onValueChange={setSelectedOC} required>
                <SelectTrigger><SelectValue placeholder="Select Operating Concern" /></SelectTrigger>
                <SelectContent>
                  {data.operatingConcerns.map(oc => (
                    <SelectItem key={oc.operating_concern_id} value={oc.operating_concern_id}>
                      {oc.operating_concern_id} - {oc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="operating_concern_id" value={selectedOC} />
            </div>
            <div>
              <Label>Assignment Control</Label>
              <Select value={selectedAssignmentControl} onValueChange={setSelectedAssignmentControl} required>
                <SelectTrigger><SelectValue placeholder="Select Assignment Control" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAME_AS_COMPANY_CODE">
                    Same as Company Code (1:1 - No Cross-Company CO)
                  </SelectItem>
                  <SelectItem value="CROSS_COMPANY_CODE_COST_ACCOUNTING">
                    Cross Company Code Cost Accounting (1:Many - Group Reporting)
                  </SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="assignment_control" value={selectedAssignmentControl} />
            </div>
            <div>
              <Label>Standard Cost Center Hierarchy ID</Label>
              <Input 
                name="standard_cost_center_hierarchy_id"
                defaultValue={editingItem?.standard_cost_center_hierarchy_id}
                placeholder="e.g., HIER_CARA"
                required
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Root node for the standard cost center hierarchy (required for all cost centers in this CA)
              </p>
            </div>
            <div>
              <Label>Chart of Accounts</Label>
              <Select value={selectedCOA} onValueChange={setSelectedCOA} required>
                <SelectTrigger><SelectValue placeholder="Select COA" /></SelectTrigger>
                <SelectContent>
                  {chartOfAccounts.map(coa => (
                    <SelectItem key={coa.coa_id} value={coa.coa_id}>
                      {coa.coa_id} - {coa.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="chart_of_accounts_id" value={selectedCOA} />
            </div>
            <div>
              <Label>Fiscal Year Variant</Label>
              <Select value={selectedFYV} onValueChange={setSelectedFYV} required>
                <SelectTrigger><SelectValue placeholder="Select FYV" /></SelectTrigger>
                <SelectContent>
                  {fiscalYearVariants.map(fyv => (
                    <SelectItem key={fyv.fiscal_year_variant_id} value={fyv.fiscal_year_variant_id}>
                      {fyv.fiscal_year_variant_id} - {fyv.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="fiscal_year_variant_id" value={selectedFYV} />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency} required>
                <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                <SelectContent>
                  {currencies.map(curr => (
                    <SelectItem key={curr.currency_code} value={curr.currency_code}>
                      {curr.currency_code} - {curr.currency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="currency" value={selectedCurrency} />
            </div>
            <div>
              <Label>Currency Type</Label>
              <Select value={selectedCurrencyType} onValueChange={setSelectedCurrencyType} required>
                <SelectTrigger><SelectValue placeholder="Select Currency Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 - Company Code Currency (Local)</SelectItem>
                  <SelectItem value="20">20 - Controlling Area Currency</SelectItem>
                  <SelectItem value="30">30 - Group Currency (Consolidation)</SelectItem>
                  <SelectItem value="40">40 - Hard Currency</SelectItem>
                  <SelectItem value="50">50 - Index Currency</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="currency_type" value={selectedCurrencyType} />
            </div>
          </>
        );

      case 'cost-center-groups':
        return (
          <>
            <div>
              <Label>Group ID</Label>
              <Input 
                name="group_id"
                defaultValue={editingItem?.group_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., CCG001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Controlling Area</Label>
              <Select value={selectedCA} onValueChange={setSelectedCA} required disabled={!!editingItem}>
                <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                <SelectContent>
                  {data.controllingAreas.map(ca => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="controlling_area_id" value={selectedCA} />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} />
            </div>
            <div>
              <Label>Parent Group</Label>
              <Select value={selectedParentGroup} onValueChange={setSelectedParentGroup} required>
                <SelectTrigger><SelectValue placeholder="Select Parent Group" /></SelectTrigger>
                <SelectContent>
                  {data.costCenterGroups.filter(g => g.controlling_area_id === selectedCA).map(group => (
                    <SelectItem key={group.group_id} value={group.group_id}>
                      {group.group_id} - {group.name} {group.is_standard_root ? '(ROOT)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="parent_group_id" value={selectedParentGroup} />
            </div>
          </>
        );

      case 'cost-centers':
        return (
          <>
            <div>
              <Label>Cost Center ID</Label>
              <Input 
                name="cost_center_id"
                defaultValue={editingItem?.cost_center_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., CC001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Controlling Area</Label>
              <Select value={selectedCA} onValueChange={setSelectedCA} required disabled={!!editingItem}>
                <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                <SelectContent>
                  {data.controllingAreas.map(ca => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="controlling_area_id" value={selectedCA} />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} />
            </div>
            <div>
              <Label>Cost Center Group</Label>
              <Select value={selectedCostCenterGroup} onValueChange={setSelectedCostCenterGroup} required>
                <SelectTrigger><SelectValue placeholder="Select Cost Center Group" /></SelectTrigger>
                <SelectContent>
                  {data.costCenterGroups.filter(g => g.controlling_area_id === selectedCA).map(group => (
                    <SelectItem key={group.group_id} value={group.group_id}>
                      {group.group_id} - {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="group_id" value={selectedCostCenterGroup} />
            </div>
            <div>
              <Label>Profit Center (optional)</Label>
              <Select value={selectedProfitCenter} onValueChange={setSelectedProfitCenter}>
                <SelectTrigger><SelectValue placeholder="Select Profit Center (optional)" /></SelectTrigger>
                <SelectContent>
                  {data.profitCenters.filter(pc => pc.controlling_area_id === selectedCA).map(pc => (
                    <SelectItem key={pc.profit_center_id} value={pc.profit_center_id}>
                      {pc.profit_center_id} - {pc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProfitCenter && <input type="hidden" name="profit_center_id" value={selectedProfitCenter} />}
            </div>
            <div>
              <Label>Valid From</Label>
              <Input name="valid_from" type="date" defaultValue={editingItem?.valid_from} required />
            </div>
            <div>
              <Label>Valid To (optional)</Label>
              <Input name="valid_to" type="date" defaultValue={editingItem?.valid_to} />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory} required>
                <SelectTrigger><SelectValue placeholder="Select Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Production">Production</SelectItem>
                  <SelectItem value="Sales">Sales</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="R&D">R&D</SelectItem>
                  <SelectItem value="Support">Support</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="category" value={selectedCategory} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency} required>
                <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                <SelectContent>
                  {currencies.map(curr => (
                    <SelectItem key={curr.currency_code} value={curr.currency_code}>
                      {curr.currency_code} - {curr.currency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="currency" value={selectedCurrency} />
            </div>
            <div>
              <Label>Person Responsible (optional)</Label>
              <Select value={selectedPersonResponsible || 'none'} onValueChange={(v) => setSelectedPersonResponsible(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="cc-person-responsible-select"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id || e.id} value={e.employee_id || e.id}>
                      {e.employee_id || e.id} - {e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="person_responsible_id" value={selectedPersonResponsible} />
            </div>
          </>
        );

      case 'profit-center-groups':
        return (
          <>
            <div>
              <Label>Group ID</Label>
              <Input 
                name="group_id"
                defaultValue={editingItem?.group_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., PCG001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Controlling Area</Label>
              <Select value={selectedCA} onValueChange={setSelectedCA} required disabled={!!editingItem}>
                <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                <SelectContent>
                  {data.controllingAreas.map(ca => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="controlling_area_id" value={selectedCA} />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} />
            </div>
            <div>
              <Label>Parent Group</Label>
              <Select value={selectedParentGroup} onValueChange={setSelectedParentGroup} required>
                <SelectTrigger><SelectValue placeholder="Select Parent Group" /></SelectTrigger>
                <SelectContent>
                  {data.profitCenterGroups.filter(g => g.controlling_area_id === selectedCA && g.group_id !== editingItem?.group_id).map(group => (
                    <SelectItem key={group.group_id} value={group.group_id}>
                      {group.group_id} - {group.name} {group.is_standard_root ? '(ROOT)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="parent_group_id" value={selectedParentGroup} />
            </div>
          </>
        );

      case 'profit-centers':
        return (
          <>
            <div>
              <Label>Profit Center ID</Label>
              <Input 
                name="profit_center_id"
                defaultValue={editingItem?.profit_center_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., PC001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Controlling Area</Label>
              <Select value={selectedCA} onValueChange={setSelectedCA} required disabled={!!editingItem}>
                <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                <SelectContent>
                  {data.controllingAreas.map(ca => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="controlling_area_id" value={selectedCA} />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} />
            </div>
            <div>
              <Label>Profit Center Group</Label>
              <Select value={selectedProfitCenterGroup} onValueChange={setSelectedProfitCenterGroup} required>
                <SelectTrigger><SelectValue placeholder="Select Group" /></SelectTrigger>
                <SelectContent>
                  {data.profitCenterGroups.filter(g => g.controlling_area_id === selectedCA).map(group => (
                    <SelectItem key={group.group_id} value={group.group_id}>
                      {group.group_id} - {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="group_id" value={selectedProfitCenterGroup} />
            </div>
            <div>
              <Label>Valid From</Label>
              <Input name="valid_from" type="date" defaultValue={editingItem?.valid_from} required />
            </div>
            <div>
              <Label>Valid To (optional)</Label>
              <Input name="valid_to" type="date" defaultValue={editingItem?.valid_to} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency} required>
                <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                <SelectContent>
                  {currencies.map(curr => (
                    <SelectItem key={curr.currency_code} value={curr.currency_code}>
                      {curr.currency_code} - {curr.currency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="currency" value={selectedCurrency} />
            </div>
            <div>
              <Label>Person Responsible (optional)</Label>
              <Select value={selectedPersonResponsible || 'none'} onValueChange={(v) => setSelectedPersonResponsible(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="pc-person-responsible-select"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id || e.id} value={e.employee_id || e.id}>
                      {e.employee_id || e.id} - {e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="person_responsible_id" value={selectedPersonResponsible} />
            </div>
            <div>
              <Label>Segment ID (optional)</Label>
              <Select value={selectedSegment || 'none'} onValueChange={(v) => setSelectedSegment(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="pc-segment-select"><SelectValue placeholder="Select Segment" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {segments.map((s) => (
                    <SelectItem key={s.segment_id} value={s.segment_id}>
                      {s.segment_id} - {s.description || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="segment_id" value={selectedSegment} />
            </div>
          </>
        );

      case 'internal-orders':
        return (
          <>
            <div>
              <Label>Internal Order ID</Label>
              <Input 
                name="internal_order_id"
                defaultValue={editingItem?.internal_order_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., IO001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Controlling Area</Label>
              <Select value={selectedCA} onValueChange={setSelectedCA} required disabled={!!editingItem}>
                <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                <SelectContent>
                  {data.controllingAreas.map(ca => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="controlling_area_id" value={selectedCA} />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required />
            </div>
            <div>
              <Label>Order Type</Label>
              <Select value={selectedOrderType} onValueChange={setSelectedOrderType} required>
                <SelectTrigger data-testid="io-order-type-select"><SelectValue placeholder="Select Order Type" /></SelectTrigger>
                <SelectContent>
                  {ORDER_TYPES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="order_type" value={selectedOrderType} />
            </div>
            <div>
              <Label>Assigned Cost Center</Label>
              <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter} required>
                <SelectTrigger><SelectValue placeholder="Select Cost Center" /></SelectTrigger>
                <SelectContent>
                  {data.costCenters.map(cc => (
                    <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>
                      {cc.cost_center_id} - {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="assigned_cost_center_id" value={selectedCostCenter} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
          </>
        );

      case 'statistical-key-figures':
        return (
          <>
            <div>
              <Label>SKF ID</Label>
              <Input 
                name="skf_id"
                defaultValue={editingItem?.skf_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., SKF001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Controlling Area</Label>
              <Select value={selectedCA} onValueChange={setSelectedCA} required disabled={!!editingItem}>
                <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                <SelectContent>
                  {data.controllingAreas.map(ca => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="controlling_area_id" value={selectedCA} />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} required />
            </div>
            <div>
              <Label>Unit of Measure</Label>
              <Select value={selectedUOM} onValueChange={setSelectedUOM} required>
                <SelectTrigger data-testid="skf-uom-select"><SelectValue placeholder="Select UOM" /></SelectTrigger>
                <SelectContent>
                  {unitsOfMeasure.map((u) => (
                    <SelectItem key={u.unit_code} value={u.unit_code}>
                      {u.unit_code} - {u.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="unit_of_measure" value={selectedUOM} />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const getCurrentData = () => {
    const dataMap = {
      'operating-concerns': data.operatingConcerns,
      'controlling-areas': data.controllingAreas,
      'cost-center-groups': data.costCenterGroups,
      'cost-centers': data.costCenters,
      'profit-center-groups': data.profitCenterGroups,
      'profit-centers': data.profitCenters,
      'internal-orders': data.internalOrders,
      'statistical-key-figures': data.statisticalKeyFigures,
    };
    return dataMap[activeTab] || [];
  };

  const getTableColumns = () => {
    const columnsMap = {
      'operating-concerns': ['ID', 'Name', 'Description', 'Actions'],
      'controlling-areas': ['ID', 'Name', 'Operating Concern', 'COA', 'FYV', 'Currency', 'Currency Type', 'Assignment Control', 'Std. CC Hier.', 'Actions'],
      'cost-center-groups': ['Group ID', 'Name', 'Controlling Area', 'Parent Group', 'Description', 'Actions'],
      'cost-centers': ['ID', 'Name', 'CA', 'Group', 'Profit Center', 'Category', 'Status', 'Valid From', 'Actions'],
      'profit-center-groups': ['Group ID', 'Name', 'Controlling Area', 'Parent Group', 'Description', 'Actions'],
      'profit-centers': ['ID', 'Name', 'CA', 'Group', 'Status', 'Currency', 'Valid From', 'Actions'],
      'internal-orders': ['ID', 'Name', 'Controlling Area', 'Cost Center', 'Status', 'Actions'],
      'statistical-key-figures': ['ID', 'Description', 'Controlling Area', 'Unit', 'Actions'],
    };
    return columnsMap[activeTab] || [];
  };

  const getTableRow = (item) => {
    switch (activeTab) {
      case 'operating-concerns':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.operating_concern_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-sm">{item.description || '-'}</td>
          </>
        );
      case 'controlling-areas':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.controlling_area_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.operating_concern_id}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.chart_of_accounts_id}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.fiscal_year_variant_id}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.currency}</td>
            <td className="py-3 px-4 text-xs">
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                {item.currency_type === '10' ? 'CC Curr' : 
                 item.currency_type === '20' ? 'CA Curr' : 
                 item.currency_type === '30' ? 'Grp Curr' : 
                 item.currency_type === '40' ? 'Hard' : 
                 item.currency_type === '50' ? 'Index' : item.currency_type}
              </span>
            </td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${
                item.assignment_control === 'SAME_AS_COMPANY_CODE' 
                  ? 'bg-gray-100 text-gray-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {item.assignment_control === 'SAME_AS_COMPANY_CODE' ? '1:1' : '1:N'}
              </span>
            </td>
            <td className="py-3 px-4 font-mono text-xs">{item.standard_cost_center_hierarchy_id}</td>
          </>
        );
      case 'cost-center-groups':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.group_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.controlling_area_id}</td>
            <td className="py-3 px-4 font-mono text-xs">
              {item.parent_group_id}
              {item.is_standard_root && <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">ROOT</span>}
            </td>
            <td className="py-3 px-4 text-xs text-gray-600">{item.description || '-'}</td>
          </>
        );
      case 'cost-center-hierarchy':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.node_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-sm">{item.node_type}</td>
            <td className="py-3 px-4 text-sm">{item.controlling_area_id}</td>
          </>
        );
      case 'cost-centers':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.cost_center_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.controlling_area_id}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.group_id}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.profit_center_id}</td>
            <td className="py-3 px-4 text-xs">
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                {item.category}
              </span>
            </td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${
                item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {item.status}
              </span>
            </td>
            <td className="py-3 px-4 text-xs">{item.valid_from}</td>
          </>
        );
      case 'profit-center-groups':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.group_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.controlling_area_id}</td>
            <td className="py-3 px-4 font-mono text-xs">
              {item.parent_group_id || '-'}
              {item.is_standard_root && <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">ROOT</span>}
            </td>
            <td className="py-3 px-4 text-xs text-gray-600">{item.description || '-'}</td>
          </>
        );
      case 'profit-centers':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.profit_center_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.controlling_area_id}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.group_id}</td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${
                item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {item.status}
              </span>
            </td>
            <td className="py-3 px-4 font-mono text-xs">{item.currency}</td>
            <td className="py-3 px-4 text-xs">{item.valid_from}</td>
          </>
        );
      case 'internal-orders':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.internal_order_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-sm">{item.controlling_area_id}</td>
            <td className="py-3 px-4 text-sm">{item.assigned_cost_center_id}</td>
            <td className="py-3 px-4 text-sm">{item.status}</td>
          </>
        );
      case 'statistical-key-figures':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.skf_id}</td>
            <td className="py-3 px-4 text-sm">{item.description}</td>
            <td className="py-3 px-4 text-sm">{item.controlling_area_id}</td>
            <td className="py-3 px-4 text-sm">{item.unit_of_measure}</td>
          </>
        );
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'operating-concerns', label: 'Operating Concerns' },
    { id: 'controlling-areas', label: 'Controlling Areas' },
    { id: 'cost-center-groups', label: 'Cost Center Groups' },
    { id: 'cost-centers', label: 'Cost Centers' },
    { id: 'profit-center-groups', label: 'Profit Center Groups' },
    { id: 'profit-centers', label: 'Profit Centers' },
    { id: 'internal-orders', label: 'Internal Orders' },
    { id: 'statistical-key-figures', label: 'Statistical Key Figures' },
    { id: 'cost-elements', label: 'Cost Elements', link: '/controlling/cost-elements' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Controlling (CO)</h2>
          <p className="text-sm text-gray-500 mt-1">Manage controlling module entities</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  if (tab.link) {
                    navigate(tab.link);
                  } else {
                    setActiveTab(tab.id);
                    setEditingItem(null);
                    setIsDialogOpen(false);
                  }
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              {canWrite(activeTab) && (
                <DialogTrigger asChild>
                  <Button type="button" data-testid="btn-add-new" onClick={() => setEditingItem(null)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className="max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} {tabs.find(t => t.id === activeTab)?.label}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {getFormFields()}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingItem(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {getTableColumns().map((col, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getCurrentData().length === 0 ? (
                  <tr>
                    <td colSpan={getTableColumns().length} className="text-center py-8 text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  getCurrentData().map((item) => (
                    <tr key={item[getIdField()]} className="border-b border-gray-100 hover:bg-gray-50">
                      {getTableRow(item)}
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <RecordActions
                            doctype={activeTab}
                            idForTestId={item[getIdField()]}
                            onView={() => setViewItem(item)}
                            onEdit={() => { setEditingItem(item); setIsDialogOpen(true); }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title={`${tabs.find(t => t.id === activeTab)?.label || 'Record'} Details`}
        record={viewItem}
      />
    </Layout>
  );
};

export default Controlling;
